<template>
  <Modal
    :title="$t('movement.create.modalTitle')"
    name="SERVICE_PROVIDER_ADD_BALANCE_MODAL"
    width="350"
    actions
  >
    <template v-slot:body>
      <v-form v-model="valid" ref="addMovementForm" lazy-validation>
        <h6 class="text-uppercase grey--text">
          {{ $t('movement.create.availableBalance') }}
        </h6>
        <p class="body-2">{{ availableBalance }}</p>

        <h6 class="text-uppercase grey--text">
          {{ $t('movement.create.sendTo') }}
        </h6>
        <p class="body-2 mb-8">{{ customerName }}</p>

        <v-text-field
          v-model.lazy="value"
          v-money="catalogCurrency()"
          :label="$t('movement.create.value')"
          :rules="[rules.required]"
          hide-details="auto"
          class="amount mb-5"
          outlined
        />

        <v-textarea
          v-model="description"
          :rules="[rules.required]"
          hide-details
          outlined
          :label="$t('movement.create.description')"
          class="amount mb-5"
          rows="3"
        />

        <v-autocomplete
          v-model="category"
          :label="$t('movement.label.category')"
          :items="categories"
          item-text="name"
          item-value="value"
          :rules="[rules.required]"
          @input="sortReasonsByCategory"
          outlined
        />

        <v-autocomplete
          v-model="reason"
          :label="$t('movement.label.reason')"
          :items="reasons"
          item-text="name"
          item-value="value"
          :rules="[rules.required]"
          :disabled="!category"
          outlined
        />
      </v-form>
    </template>

    <template v-slot:actions>
      <v-btn
        @click="handleClose"
        :disabled="isLoading"
        class="mr-5 grey--text"
        elevation="0"
        large
        text
      >
        {{ $t('movement.create.cancel') }}
      </v-btn>

      <v-btn
        @click="save"
        :loading="isLoading"
        color="primary"
        elevation="0"
        large
      >
        {{ $t('movement.create.confirmButton') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/app/Modal'
import { mapActions } from 'vuex'
import { clearCurrency } from '@/util/helpers'
import { catalogMonetary, catalogCurrency } from '@/util/catalog'
import { required, minLength } from '@/util/errorHandling'
import { VMoney } from 'v-money'
import { categories, reasonsByCategory } from '@/components/movement/movementCategoriesAndReasons'

export default {
  name: 'ServiceProviderAddBalanceModal',
  directives: { money: VMoney },
  components: { Modal },

  props: {
    client: { type: Object, default: null }
  },

  data() {
    return {
      valid: false,
      isLoading: false,
      value: 0,
      description: '',
      categories,
      reasonsByCategory,
      reasons: [],
      category: null,
      reason: null,
      rules: { required, minLength },
      catalogCurrency,
    }
  },

  computed: {
    availableBalance() {
      const amount = this.client?.balance
      return catalogMonetary(amount ? amount : 0)
    },
    customerName() {
      return this.client?.name || '-'
    },
    realEstateId() {
      return this.client?.id || null
    }
  },

  watch: {
    client: {
      immediate: true,
      handler() {
        this.resetForm()
      }
    }
  },

  methods: {
    ...mapActions([
      'closeModal',
      'serviceProviderAddBalance',
      'listServiceProviderCustomers',
      'showErrorMessage',
      'showSuccessMessage',
    ]),

    handleClose() {
      this.closeModal('SERVICE_PROVIDER_ADD_BALANCE_MODAL')
      this.resetForm()
    },

    sortReasonsByCategory() {
      this.reasons = this.reasonsByCategory?.[this.category] || []
      this.reason = null
    },

    resetForm() {
      this.description = ''
      this.value = 0
      this.category = null
      this.reason = null
      this.reasons = []
      this.$nextTick(() => this.$refs?.addMovementForm?.resetValidation?.())
    },

    async save() {
      if (!this.realEstateId) {
        this.showErrorMessage('Cliente inválido para alterar saldo.')
        return
      }

      if (!this.$refs.addMovementForm?.validate()) return

      this.isLoading = true

      const numericValue = clearCurrency(this.value)

      const body = {
        real_estate_id: this.realEstateId,
        value: numericValue,
        category: this.category,
        reason: this.reason,
        description: this.description,
      }

      const message =
        numericValue > 0
          ? this.$t('movement.create.messages.balanceAdded', { value: this.value })
          : this.$t('movement.create.messages.balanceRemoved', { value: this.value })

      try {
        await this.serviceProviderAddBalance(body)
        this.showSuccessMessage(message)
        this.$emit('success')
        this.handleClose()
        this.listServiceProviderCustomers()
      } catch (err) {
        const msg =
          err?.data?.errors?.[0]?.message ||
          err?.data?.message ||
          err?.message ||
          'Erro ao alterar saldo'

        this.showErrorMessage(msg)
        this.$emit('error', msg)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>
